import { logger } from '@tuic/logger';
import { Account } from '../../index';

export type BookmarkWithCreation = Account.Bookmark & { created_at: string };

/**
 * Fetches the user's bookmarks.
 */
export const getBookmarks = async (
  idToken: string,
): Promise<BookmarkWithCreation[]> => {
  let { data, error } = await Account.getBookmarks({
    auth: idToken,
  });

  if (error) {
    logger.error(`Failed fetching bookmarks. ID Token: ${idToken}`);
    logger.error(error);

    return [];
  }

  let pointZero = new Date(0).toISOString();

  return (data ?? []).map(bookmark => {
    let created_at = 'created_at' in bookmark ? bookmark.created_at : undefined;
    return {
      ...bookmark,
      created_at: created_at ? created_at.toString() : pointZero,
    };
  });
};

/**
 * Fetches a single bookmark from the user's bookmarks.
 */
export const getBookmark = async (args: {
  idToken: string;
  tripCode: string;
}): Promise<Account.Bookmark | null> => {
  let { idToken, tripCode } = args;

  let { data, error } = await Account.getBookmark({
    path: { tripCode },
    auth: idToken,
  });

  if (error) {
    logger.error(
      `Failed getting single bookmark. ID Token: ${idToken}, Trip Code: ${tripCode}`,
    );
    logger.error(error);

    return null;
  }

  return data ?? null;
};

/**
 * Checks if there is a bookmark saved for given trip code.
 */
export const hasBookmark = async (args: {
  idToken: string;
  tripCode: string;
}): Promise<boolean> => {
  let { idToken, tripCode } = args;

  let bookmark = await getBookmark({ idToken, tripCode });

  return (
    bookmark !== null &&
    typeof bookmark == 'object' &&
    Object.keys(bookmark).length > 0
  );
};

/**
 * Saves given tripCode in the user's bookmarks.
 */
export const saveBookmark = async (args: {
  idToken: string;
  tripCode: string;
  configuration?: Account.BookmarkConfiguration;
}): Promise<boolean> => {
  let { idToken, tripCode, configuration } = args;

  let { data, error } = await Account.addBookmark({
    body: {
      tripCode,
      configuration,
    },
    auth: idToken,
  });

  if (error) {
    logger.error(
      `Failed saving bookmark. ID Token: ${idToken}, Trip Code: ${tripCode}`,
    );
    logger.error(error);

    return false;
  }

  return data?.success ?? false;
};

/**
 * Deletes given tripCode in the user's bookmarks.
 */
export const deleteBookmark = async (args: {
  idToken: string;
  tripCode: string;
}): Promise<boolean> => {
  let { idToken, tripCode } = args;

  let { data, error } = await Account.deleteBookmark({
    path: {
      tripCode,
    },
    auth: idToken,
  });

  if (error) {
    logger.error(
      `Failed deleting bookmark. ID Token: ${idToken}, Trip Code: ${tripCode}`,
    );
    logger.error(error);

    return false;
  }

  return data?.success ?? false;
};
